const toTrimmedString = (value) =>
  value === null || typeof value === "undefined" ? "" : String(value).trim();

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const toMessageList = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => toMessageList(item));
  }

  if (isPlainObject(value)) {
    return Object.values(value).flatMap((item) => toMessageList(item));
  }

  const normalizedValue = toTrimmedString(value);
  return normalizedValue ? [normalizedValue] : [];
};

const getErrorPayload = (error) => error?.raw ?? error?.response?.data ?? null;

export const getErrorStatus = (error) => Number(error?.status || error?.response?.status || 0);

export const getTechnicalErrorMessage = (error) => {
  const payload = getErrorPayload(error);

  return (
    toMessageList(payload?.detail || payload?.message || payload?.error)[0] ||
    toMessageList(payload?.non_field_errors)[0] ||
    toTrimmedString(error?.technicalMessage) ||
    toTrimmedString(error?.message)
  );
};

const isNetworkOrTimeoutError = (error) => {
  const status = getErrorStatus(error);
  const errorCode = toTrimmedString(error?.code || error?.response?.code).toUpperCase();
  const message = getTechnicalErrorMessage(error).toLowerCase();

  if (!status && ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(errorCode)) {
    return true;
  }

  return (
    !status &&
    [
      "network error",
      "failed to fetch",
      "timeout",
      "timed out",
      "network request failed",
    ].some((pattern) => message.includes(pattern))
  );
};

export const matchesTechnicalError = (error, patterns = []) => {
  const candidateText = getTechnicalErrorMessage(error).toLowerCase();

  return patterns.some((pattern) => {
    if (pattern instanceof RegExp) {
      return pattern.test(candidateText);
    }

    return candidateText.includes(String(pattern).toLowerCase());
  });
};

const isServerError = (error) => getErrorStatus(error) >= 500;

export const getTeacherAuthErrorMessage = (error, flow = "default") => {
  const status = getErrorStatus(error);

  if (isNetworkOrTimeoutError(error)) {
    return "We couldn't reach the teacher portal right now. Please check your connection and try again.";
  }

  if (status === 429) {
    return "Too many attempts were detected. Please wait a moment and try again.";
  }

  if (flow === "login") {
    if (
      status === 401 ||
      status === 403 ||
      matchesTechnicalError(error, [
        "invalid credential",
        "invalid credentials",
        "incorrect password",
        "wrong password",
        "no active account",
      ])
    ) {
      return "Invalid email or password. Please try again.";
    }

    if (isServerError(error) || status === 404) {
      return "We couldn't log you in right now. Please try again in a moment.";
    }

    return "Unable to complete teacher login. Please try again.";
  }

  if (flow === "signup") {
    if (
      status === 409 ||
      matchesTechnicalError(error, [
        "already exists",
        "already registered",
        "duplicate",
        "already been taken",
      ])
    ) {
      return "A teacher account with those details already exists.";
    }

    if (isServerError(error)) {
      return "Teacher signup is temporarily unavailable. Please try again shortly.";
    }

    return "Unable to create the teacher account. Please review your details and try again.";
  }

  if (flow === "otp") {
    if (
      [400, 401, 403].includes(status) ||
      matchesTechnicalError(error, [
        "otp",
        "verification code",
        "invalid code",
        "expired code",
      ])
    ) {
      return "The verification code is invalid or expired. Please try again.";
    }

    if (isServerError(error)) {
      return "OTP verification is temporarily unavailable. Please try again shortly.";
    }

    return "Unable to verify the code right now. Please try again.";
  }

  if (flow === "forgot-password") {
    if (isServerError(error)) {
      return "Password reset is temporarily unavailable. Please try again shortly.";
    }

    return "We couldn't start the password reset request. Please check the email and try again.";
  }

  if (flow === "reset-password-token") {
    if (
      [400, 401, 403, 404].includes(status) ||
      matchesTechnicalError(error, ["token", "expired", "invalid link", "reset link"])
    ) {
      return "This reset link is invalid or expired. Please request a new one.";
    }

    if (isServerError(error)) {
      return "We couldn't validate the reset link right now. Please try again shortly.";
    }

    return "Unable to validate this reset link right now.";
  }

  if (flow === "reset-password") {
    if (
      [400, 401, 403, 404].includes(status) ||
      matchesTechnicalError(error, ["token", "expired", "invalid link", "reset link"])
    ) {
      return "This reset link is invalid or expired. Please request a new one.";
    }

    if (isServerError(error)) {
      return "We couldn't reset the password right now. Please try again shortly.";
    }

    return "Unable to reset the password. Please try again.";
  }

  return "Something went wrong. Please try again.";
};

export const getClassBookingQueryErrorMessage = (error, resource = "default") => {
  const status = getErrorStatus(error);

  if (isNetworkOrTimeoutError(error)) {
    return "We couldn't reach the class booking service. Please check your connection and try again.";
  }

  if (status === 401 || status === 403) {
    return resource === "bookings"
      ? "Please log in again to view your bookings."
      : "Please log in again to continue with class booking.";
  }

  if (resource === "tutor-detail" && status === 404) {
    return "This tutor profile is no longer available.";
  }

  if (resource === "availability" && status === 404) {
    return "Tutor availability is not available right now.";
  }

  if (resource === "bookings" && status === 404) {
    return "We couldn't find any bookings to show right now.";
  }

  if (isServerError(error)) {
    return "Class booking is temporarily unavailable. Please try again shortly.";
  }

  if (resource === "tutors") {
    return "Unable to load tutors right now.";
  }

  if (resource === "tutor-detail") {
    return "Unable to load tutor details right now.";
  }

  if (resource === "availability") {
    return "Unable to load tutor availability right now.";
  }

  if (resource === "bookings") {
    return "Unable to load bookings right now.";
  }

  return "Something went wrong. Please try again.";
};

export const getClassBookingActionErrorMessage = (error, action = "default") => {
  const status = getErrorStatus(error);

  if (isNetworkOrTimeoutError(error)) {
    return "We couldn't reach the class booking service. Please try again.";
  }

  if (status === 401 || status === 403) {
    return "Student authentication is required. Please log in again.";
  }

  if (action === "hold-slot") {
    if (
      [400, 404, 409].includes(status) ||
      matchesTechnicalError(error, [
        "not active",
        "no longer available",
        "already reserved",
        "already booked",
      ])
    ) {
      return "That slot is no longer available. Please choose another time.";
    }

    if (isServerError(error)) {
      return "We couldn't reserve this slot right now. Please try again shortly.";
    }

    return "Unable to hold the booking slot right now.";
  }

  if (action === "checkout-session") {
    if (
      status === 409 &&
      matchesTechnicalError(error, ["already been paid", "already paid"])
    ) {
      return "This session has already been paid.";
    }

    if (status === 409) {
      return "This booking slot is no longer available. Please choose another time.";
    }

    if (status === 404) {
      return "We couldn't start payment for this booking. Please choose the slot again.";
    }

    if (isServerError(error)) {
      return "We couldn't start secure checkout right now. Please try again shortly.";
    }

    return "Unable to create the checkout session right now.";
  }

  if (action === "confirm-booking") {
    if (status === 404) {
      return "We couldn't find this booking confirmation. Please return to your bookings dashboard.";
    }

    if (isServerError(error)) {
      return "We couldn't confirm the booking payment right now. Please try again shortly.";
    }

    return "We could not confirm the booking payment.";
  }

  return "Something went wrong. Please try again.";
};
